<template>
  <button
    type="button"
    class="flex flex-col gap-2.5 min-w-0 p-3.5 pb-3 text-left
      bg-card border border-border rounded-ds-md cursor-pointer
      transition-[border-color,box-shadow] duration-ds-fast ease-ds-standard
      hover:border-input hover:shadow-ds-sm
      focus-visible:outline-none focus-visible:shadow-ds-focus
      disabled:pointer-events-none disabled:opacity-50"
    :disabled="disabled"
    @click="emit('select', institution)"
  >
    <div class="flex items-start justify-between">
      <!--
        Logos are institution artwork on a white plate, so the plate stays white
        in both themes rather than following the card surface — the marks are
        drawn for light grounds and vanish on a dark one.
      -->
      <span
        class="inline-flex shrink-0 items-center justify-center size-10 p-1
          bg-white border border-border rounded-ds-sm overflow-hidden"
      >
        <img
          :src="logo"
          :alt="institution.name"
          class="size-full object-contain"
          @error="handleLogoError"
        >
      </span>
      <ArrowRight class="size-4 text-muted-foreground" />
    </div>
    <div class="min-w-0">
      <div
        class="truncate text-sm font-bold text-foreground"
        :title="institution.name"
      >
        {{ institution.name }}
      </div>
      <div class="flex items-center gap-1.5 mt-0.5 text-xs font-medium text-muted-foreground">
        <!--
          `text-success` is the status fill, which is below 4.5:1 on white and
          so may not carry text. As a glyph it only needs 3:1 (WCAG 1.4.11) and
          the source name beside it is the accessible label. Shown only for open
          banking, which is what the shield is actually asserting.
        -->
        <ShieldCheck
          v-if="institution.open_banking"
          class="size-3 shrink-0 text-success"
        />
        <span class="truncate capitalize">{{ institution.source }}</span>
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowRight, ShieldCheck } from '@lucide/vue'

import { InstatutionData } from '@/views/LinkedData/utils/types'

import { INSTITUTION_LOGO_PLACEHOLDER } from '@/views/LinkedData/utils/const'

const { institution } = defineProps<{
  institution: InstatutionData
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'select', institution: InstatutionData): void
}>()

const logo = computed(() => institution.logo || INSTITUTION_LOGO_PLACEHOLDER)

const handleLogoError = (event: Event) => {
  ;(event.target as HTMLImageElement).src = INSTITUTION_LOGO_PLACEHOLDER
}
</script>
